import { useState, type FormEvent } from "react";

import { refusalText } from "../../../../api/errors";
import type { Id, SymbolConversion } from "../../../../api/types";
import { Button, Card, ShapeDrawing } from "../../../../ui";
import { useApiUpload, useCommandId, useRefresh } from "../../hooks";
import { FormError, Select, TextInput } from "../fields";
import { codeFrom, drawingSuffix } from "./words";

interface Drawing {
  id: Id;
  code: string;
  name: string;
  deliverableKind: string | null;
}

/**
 * The upload of a drawing: the app turns the SVG into a shape and shows it with what the engineer should know (a
 * drawing with no size asks for its unit). Only the shape is kept, never the SVG. Once stored, the shape opens in the
 * library so its connection points can be placed.
 */
export function SymbolUpload({ drawings, loading, onStored }: { drawings: Drawing[]; loading: boolean; onStored: (code: string, warnings: string[]) => void }) {
  const [file, setFile] = useState<File | null>(null);
  // a new key empties the file box after a store, so that the same file can be chosen again for another shape
  const [fileKey, setFileKey] = useState(0);
  const [unit, setUnit] = useState("1");
  const [preview, setPreview] = useState<SymbolConversion | null>(null);
  const [kind, setKind] = useState<"symbol" | "line_style">("symbol");
  const [documentKind, setDocumentKind] = useState("");
  const [title, setTitle] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const convert = useApiUpload("convert_symbol");
  const upload = useApiUpload("upload_symbol");
  const convertIds = useCommandId();
  const ids = useCommandId();
  const refresh = useRefresh();
  const drawing = drawings.find((d) => d.id === documentKind);
  const suffix = drawing ? `_${drawingSuffix(drawing.code)}` : "";
  const unitMm = Number(unit);

  const look = (chosen: File | null, unitText: string) => {
    setPreview(null);
    if (!chosen) return;
    const value = Number(unitText);
    convert.mutate(
      { file: chosen, input: { command_id: convertIds.current(), unit_mm: Number.isFinite(value) && value > 0 ? value : null } },
      { onSuccess: (reply) => setPreview(reply.data), onSettled: () => convertIds.renew() },
    );
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (!file) found.file = "Choose the drawing: an SVG file.";
    if (!code.trim()) found.code = "Give it a code.";
    if (!documentKind) found.documentKind = "Choose the kind of drawing it is for.";
    if (preview?.needs_unit && !(unitMm > 0)) found.unit = "Say how long one unit of the drawing is, in millimetres.";
    setErrors(found);
    if (Object.keys(found).length > 0 || !file) return;
    upload.mutate(
      {
        file,
        input: {
          command_id: ids.current(),
          code: code.trim(),
          document_kind_id: documentKind,
          kind,
          unit_mm: preview?.needs_unit ? unitMm : null,
          title: title.trim() || null,
          description: description.trim() || null,
          category: category.trim() || null,
        },
      },
      {
        onSuccess: (reply) => {
          ids.renew();
          setFile(null);
          setFileKey((n) => n + 1);
          setPreview(null);
          setCode("");
          setTitle("");
          setDescription("");
          setCategory("");
          refresh("symbol_set", "item_page", "item_snapshot");
          onStored(reply.data.code, reply.data.warnings ?? []);
        },
      },
    );
  };

  return (
    <Card aria-labelledby="upload-symbol-title">
      <h2 id="upload-symbol-title">Upload a drawing</h2>
      <p className="muted">
        The app turns an SVG drawing into a shape in millimetres and keeps only the shape. A drawing that holds pictures, scripts or anything a symbol cannot hold is refused, with
        the reason; nothing is cut out of it silently.
      </p>
      <form onSubmit={submit} noValidate className="stack" aria-label="Upload a symbol">
        <div className="field">
          <label htmlFor="symbol-file" className="field__label">
            The drawing <span className="field__required">(needed)</span>
          </label>
          <p className="field__hint muted">An SVG file.</p>
          <input
            key={fileKey}
            id="symbol-file"
            type="file"
            className="field__input"
            accept=".svg,image/svg+xml"
            aria-invalid={errors.file ? true : undefined}
            onChange={(event) => {
              const chosen = event.target.files?.[0] ?? null;
              setFile(chosen);
              look(chosen, unit);
            }}
          />
          {errors.file && (
            <p role="alert" className="field__error">
              {errors.file}
            </p>
          )}
        </div>
        {convert.isPending && <p className="muted">Reading the drawing</p>}
        <FormError message={convert.error ? refusalText(convert.error) : null} />
        {preview && (
          <div className="symbol-preview" aria-label="The shape the drawing gives">
            <ShapeDrawing shape={preview.shape} kind="symbol" label="The shape the drawing gives" size={160} />
            <div className="stack">
              {preview.warnings.length > 0 && (
                <ul className="muted" aria-label="What to know about the drawing">
                  {preview.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              )}
              {preview.needs_unit && (
                <>
                  <TextInput label="One unit of the drawing is (mm)" required hint="The drawing gives no size. 1 when it was drawn in millimetres." value={unit} error={errors.unit} onChange={setUnit} />
                  <Button variant="quiet" onClick={() => look(file, unit)} busy={convert.isPending}>
                    Look again with this unit
                  </Button>
                </>
              )}
              {kind === "symbol" && (
                <p className="muted">Once it is stored, place its connection points in the library above. For a valve, the app can offer the two ends of its flow axis.</p>
              )}
            </div>
          </div>
        )}
        <Select
          label="It is a"
          value={kind}
          options={[
            { value: "symbol", label: "Symbol of an item or component" },
            { value: "line_style", label: "Line style of a Connection" },
          ]}
          onChange={(value) => setKind(value as "symbol" | "line_style")}
        />
        <Select
          label="For which kind of drawing"
          required
          value={documentKind}
          placeholder={loading ? "Reading the kinds of drawing" : "Choose the kind of drawing"}
          options={drawings.map((d) => ({ value: d.id, label: d.name }))}
          error={errors.documentKind}
          onChange={setDocumentKind}
        />
        {!loading && drawings.length === 0 && <p className="muted">No kind B drawing is stored yet. Add a document kind that is a drawing of kind B first.</p>}
        <TextInput
          label="Name"
          hint="What it shows, for example Gate valve."
          value={title}
          onChange={(value) => {
            setTitle(value);
            if (!code || code === codeFrom(title) + suffix) setCode(value.trim() ? codeFrom(value) + suffix : "");
          }}
        />
        <TextInput label="Code" required hint={`The name the symbol is known by, unique in the library${suffix ? `; it ends in ${suffix}` : ""}.`} value={code} error={errors.code} onChange={setCode} />
        <TextInput label="Description" hint="Where it is used, in plain words (optional)." value={description} onChange={setDescription} />
        <TextInput label="Category" hint="A heading for browsing, for example valve (optional)." value={category} onChange={setCategory} />
        <div className="row">
          <Button type="submit" variant="primary" busy={upload.isPending}>
            Store the shape
          </Button>
        </div>
        <FormError message={upload.error ? refusalText(upload.error) : null} />
      </form>
    </Card>
  );
}
